import { getFullName } from '@ben/common/names';
import RiArrowDownSLine from '@ben/icons/arrow-down-s-line';
import RiExternalLinkLine from '@ben/icons/external-link-line';
import RiLogoutBoxRLine from '@ben/icons/logout-box-r-line';
import RiUserCommunityLine from '@ben/icons/user-community-line';
import RiUserLine from '@ben/icons/user-line';
import RiUserShared2Line from '@ben/icons/user-shared-2-line';
import RiWalletLine from '@ben/icons/wallet-line';
import { useLeftNavigationTheme } from '@ben/orion/hooks/use-left-navigation-theme';
import MantineTooltip from '@ben/orion/mantine-tooltip';
import Menu from '@ben/orion/menu';
import RIcon from '@ben/orion/remix-icon';
import { TruncatedText } from '@ben/orion/truncated-text';
import { TypographyVariant } from '@ben/orion/typography';
import { adminUrl } from '@ben/orion/utils/admin-url';
import type { ITheme } from '@ben/orion/utils/theme';
import type { MenuProps } from '@mantine/core';
import { UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import type { BgColorVariant } from '../types';
import EmployeeAvatar from './employee-avatar';
import TooltipLabel from './tooltip-label';

export interface Employee {
  avatar?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface EmployeeMenuProps extends MenuProps {
  employee: Employee;
  showSwitchToAdminView?: boolean;
  showSwitchToEmployeeView?: boolean;
  showProfile?: boolean;
  onOpenPaySummary?: () => void;
  onSignOut: () => void;
  onShowProfile?: () => void;
  showLifeEvent?: boolean;
}

const EmployeeMenu = ({
  employee,
  showSwitchToAdminView,
  showSwitchToEmployeeView,
  onOpenPaySummary,
  onSignOut,
  showLifeEvent = false,
  showProfile,
  onShowProfile,
  ...rest
}: EmployeeMenuProps) => {
  const { avatar, email } = employee;
  const { t } = useTranslation();
  const { bgColorVariant } = useLeftNavigationTheme();
  const [opened, { toggle }] = useDisclosure();
  const name = getFullName(employee);

  return (
    <Menu
      {...rest}
      position="bottom-start"
      opened={opened}
      onChange={toggle}
      width={282}
    >
      <MantineTooltip
        label={<TooltipLabel email={email} name={name} />}
        disabled={opened}
        position="bottom-start"
      >
        <StyledMenuTarget data-testid="employee-menu-target">
          <EmployeeButton
            aria-label={`${t('leftNavigation.manageAccount')} ${name}`}
            forwardedAs={undefined}
            $bgColorVariant={bgColorVariant}
            $menuOpened={opened}
          >
            <EmployeeAvatar name={name} avatar={avatar} />
            <DropdownIcon size="md" icon={RiArrowDownSLine} />
          </EmployeeButton>
        </StyledMenuTarget>
      </MantineTooltip>
      <Menu.Dropdown>
        <StyledMenuLabel>
          <EmployeeAvatar name={name} avatar={avatar} />
          <NameAndEmail>
            <TruncatedText
              tooltipProps={{ wordBreak: 'break-all' }}
              showTooltip
              variant={TypographyVariant.bodyMd3}
            >
              {name}
            </TruncatedText>
            <TruncatedText
              tooltipProps={{ wordBreak: 'break-all' }}
              showTooltip
            >
              {email}
            </TruncatedText>
          </NameAndEmail>
        </StyledMenuLabel>
        {showProfile && (
          <>
            <Menu.Divider />
            <Menu.Item
              leftSection={<RIcon icon={RiUserLine} />}
              onClick={onShowProfile}
            >
              {t('leftNavigation.profile')}
            </Menu.Item>
          </>
        )}
        {showSwitchToAdminView && (
          <>
            <Menu.Divider />
            <Menu.Item
              component="a"
              href={adminUrl}
              target="_blank"
              leftSection={<RIcon icon={RiUserShared2Line} />}
              rightSection={<RIcon icon={RiExternalLinkLine} />}
            >
              {t('leftNavigation.switchToAdminView')}
            </Menu.Item>
          </>
        )}
        {showSwitchToEmployeeView && (
          <>
            <Menu.Divider />
            <Menu.Item
              component="a"
              href={process.env.REACT_APP_COCKPIT_BASE_URL ?? '/'}
              target="_blank"
              leftSection={<RIcon icon={RiUserShared2Line} />}
              rightSection={<RIcon icon={RiExternalLinkLine} />}
            >
              {t('leftNavigation.switchToEmployeeView')}
            </Menu.Item>
          </>
        )}
        {onOpenPaySummary && (
          <>
            <Menu.Divider />
            <Menu.Item
              leftSection={<RIcon icon={RiWalletLine} />}
              onClick={onOpenPaySummary}
            >
              {t('leftNavigation.paySummary')}
            </Menu.Item>
          </>
        )}
        {showLifeEvent && (
          <>
            <Menu.Divider />
            <Menu.Item
              component={Link}
              to="/benefits/life-event"
              leftSection={<RIcon icon={RiUserCommunityLine} />}
            >
              {t('benefits.lifeEventsButton')}
            </Menu.Item>
          </>
        )}
        <Menu.Divider />
        <Menu.Item
          onClick={() => onSignOut()}
          leftSection={<RIcon icon={RiLogoutBoxRLine} />}
        >
          {t('leftNavigation.signOut', { defaultValue: 'Sign out' })}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default EmployeeMenu;

interface VariantProps {
  theme: ITheme;
  $menuOpened: boolean;
}

type VariantStyles = Record<
  BgColorVariant,
  (props: VariantProps) => FlattenSimpleInterpolation
>;

const variantStyles: VariantStyles = {
  LIGHT: ({ theme, $menuOpened }) => css`
    border: 1px solid
      ${$menuOpened ? theme.palette.neutral[60] : theme.palette.neutral[50]};
    background: ${theme.palette.neutral['00']};
    &:hover {
      border: 1px solid ${theme.palette.neutral[60]};
    }
    color: ${theme.palette.neutral[100]};
  `,
  DARK: ({ theme, $menuOpened }) => css`
    border: 1px solid theme.palette.white(0.1);
    background: ${$menuOpened
      ? theme.palette.white(0.25)
      : theme.palette.white(0.1)};
    &:hover {
      background: theme.palette.white(0.1);
    }
    color: ${theme.palette.neutral['00']};
  `,
};

const EmployeeButton = styled(UnstyledButton)`
  ${({
    theme,
    $menuOpened,
    $bgColorVariant,
  }: VariantProps & { $bgColorVariant: BgColorVariant }) => css`
    border-radius: 24px;
    width: 82px;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    padding: ${theme.spacing(0, 1, 0, 1)};
    ${variantStyles[$bgColorVariant]?.({ theme, $menuOpened })};
  `}
`;

const DropdownIcon = styled(RIcon)`
  transition: transform 150ms ease-in-out;
`;

const StyledMenuTarget = styled(Menu.Target)`
  &[aria-expanded='true'] {
    ${DropdownIcon} {
      transform: rotate(180deg);
    }
  }
`;

const StyledMenuLabel = styled(Menu.Label)`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const NameAndEmail = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin-left: ${theme.spacing(1)};
    width: 200px;
  `}
`;
